/**
 * settingsService.ts
 * Wraps Office.context.document.settings to provide typed CRUD
 * for per-slide activity configurations.
 *
 * Settings are stored as JSON strings under string keys.
 * All writes call settings.saveAsync() to persist to the document file.
 */

import type { ActivityConfig } from '@types-local/quiz.types';
import type { SlideMetadata } from '@types-local/office.types';
import { buildSlideSettingsKey, generateSlideId } from '@utils/slideId';

const META_INDEX_KEY = 'gh_slide_meta_index'; // stores array of SlideMetadata
const ACTIVE_SESSION_KEY = 'gh_active_session'; // { roomCode, classId } of live room

// ---- Generic read/write helpers ----------------------------

function settingsGet<T>(key: string): T | null {
  const raw = Office.context.document.settings.get(key);
  if (raw === null || raw === undefined) return null;
  try {
    return JSON.parse(raw as string) as T;
  } catch {
    return null;
  }
}

function settingsSet(key: string, value: unknown): void {
  Office.context.document.settings.set(key, JSON.stringify(value));
}

function settingsSave(): Promise<void> {
  return new Promise((resolve, reject) => {
    Office.context.document.settings.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(`Settings save failed: ${result.error.message}`));
      } else {
        resolve();
      }
    });
  });
}

/**
 * Reloads settings from the document. Needed in the ribbon command
 * runtime, which has its own cached copy of settings and would
 * otherwise miss values written by the task pane runtime.
 */
export function settingsRefresh(): Promise<void> {
  return new Promise((resolve) => {
    Office.context.document.settings.refreshAsync(() => resolve());
  });
}

// ---- Slide Metadata Index -----------------------------------

function getMetaIndex(): SlideMetadata[] {
  return settingsGet<SlideMetadata[]>(META_INDEX_KEY) ?? [];
}

function setMetaIndex(index: SlideMetadata[]): void {
  settingsSet(META_INDEX_KEY, index);
}

// ---- Public API --------------------------------------------

/**
 * Saves an activity config for a given slide, auto-generates a
 * slide ID if one doesn't exist yet.
 * Returns the slideId (new or existing).
 */
export async function saveSlideConfig(
  slideIndex: number,
  config: ActivityConfig,
  shapeName: string,
): Promise<string> {
  const settingsKey = buildSlideSettingsKey(slideIndex);

  // Retrieve or generate a stable slide ID
  let slideId = settingsGet<string>(`${settingsKey}_id`);
  if (!slideId) {
    slideId = generateSlideId();
    settingsSet(`${settingsKey}_id`, slideId);
  }

  // Store the config
  const configKey = `${settingsKey}_config`;
  settingsSet(configKey, config);

  // Update the metadata index
  const meta: SlideMetadata = {
    slideId,
    activityType: config.type,
    configKey,
    shapeName,
    injectedAt: Date.now(),
  };

  const index = getMetaIndex().filter((m) => m.slideId !== slideId);
  index.push(meta);
  setMetaIndex(index);

  await settingsSave();
  return slideId;
}

/**
 * Loads the activity config for a slide by slide index.
 * Returns null if no config is stored.
 */
export function loadSlideConfig(slideIndex: number): ActivityConfig | null {
  const configKey = `${buildSlideSettingsKey(slideIndex)}_config`;
  return settingsGet<ActivityConfig>(configKey);
}

/**
 * Retrieves the stored slide ID for a given index, or null.
 */
export function getStoredSlideId(slideIndex: number): string | null {
  return settingsGet<string>(`${buildSlideSettingsKey(slideIndex)}_id`);
}

/**
 * Removes all settings keys associated with a slide.
 */
export async function deleteSlideConfig(slideIndex: number): Promise<void> {
  const settingsKey = buildSlideSettingsKey(slideIndex);
  const slideId = settingsGet<string>(`${settingsKey}_id`);

  Office.context.document.settings.remove(`${settingsKey}_id`);
  Office.context.document.settings.remove(`${settingsKey}_config`);

  if (slideId) {
    const index = getMetaIndex().filter((m) => m.slideId !== slideId);
    setMetaIndex(index);
  }

  await settingsSave();
}

/**
 * Returns all slide metadata entries (all active activities in the deck).
 */
export function getAllSlideMetadata(): SlideMetadata[] {
  return getMetaIndex();
}

// ---- Active session (shared with ribbon command runtime) ----

export interface ActiveSession {
  roomCode: string;
  classId: string;
}

/**
 * Persists the live room code to document settings so the ribbon
 * command runtime (a separate context with no access to the task
 * pane's in-memory store) can read it when "Start Presenting" fires.
 */
export async function saveActiveSession(session: ActiveSession): Promise<void> {
  settingsSet(ACTIVE_SESSION_KEY, session);
  await settingsSave();
}

/**
 * Reads the live session. Call settingsRefresh() first when reading
 * from the command runtime to avoid a stale cached copy.
 */
export function loadActiveSession(): ActiveSession | null {
  return settingsGet<ActiveSession>(ACTIVE_SESSION_KEY);
}

/**
 * Clears the live session (when the teacher ends the class session).
 */
export async function clearActiveSession(): Promise<void> {
  Office.context.document.settings.remove(ACTIVE_SESSION_KEY);
  await settingsSave();
}
